import {
  ADMIN_AUTH_ERROR,
  type AdminAuthErrorCode,
  type AdminIdentity,
  type AdminLoginBody,
  type AdminLoginChallengeResponse,
  type AdminMfaUpdateBody,
  type AdminNotificationItem,
  type AdminPendingCounts,
  type AdminSessionResponse,
  type AdminTotpRefusalReason,
  type AdminTotpVerifyBody,
} from '@/contracts';

import { authenticated, renew } from '../auth-client';
import { API_PREFIX, send, type AdminApiResult } from '../http';
import { adminSession } from '../session';

/**
 * What a 401 means on a sign-in route.
 *
 * The API answers every refused credential with the envelope's `unauthenticated`, which
 * `lib/http.ts` reads as `session_invalid` because on any other route that is what it
 * is. Before a session exists it cannot be: on the password step it is the wrong
 * address or password (the code step reads its own, below). A code the envelope already
 * names (an `account_locked`, a `rate_limited`) is left as it is.
 */
function naming401(meaning: AdminAuthErrorCode) {
  return <T>(result: AdminApiResult<T>): AdminApiResult<T> =>
    !result.ok && result.status === 401 && result.error === ADMIN_AUTH_ERROR.SESSION_INVALID
      ? { ...result, error: meaning }
      : result;
}

/**
 * What a 401 means on the code step. The API names it in `details.reason`: a
 * `challenge_expired` cannot be answered by any code, so the login goes back to the
 * password; anything else — `code_invalid`, or a body without the reason — is a code
 * worth asking for again.
 */
function namingTotp401<T>(result: AdminApiResult<T>): AdminApiResult<T> {
  if (result.ok || result.status !== 401 || result.error !== ADMIN_AUTH_ERROR.SESSION_INVALID) {
    return result;
  }
  const expired: AdminTotpRefusalReason = 'challenge_expired';
  return {
    ...result,
    error:
      result.details?.reason === expired
        ? ADMIN_AUTH_ERROR.CHALLENGE_INVALID
        : ADMIN_AUTH_ERROR.INVALID_TOTP,
  };
}

export const adminApi = {
  /**
   * Step one. Normally answers with a challenge — the second factor is not
   * optional for most accounts. An admin who has turned two-factor off from
   * their portal is the exception: for them the password is the sign-in and this
   * answers with a session (an `accessToken`) directly, and sets the refresh
   * cookie as `verifyTotp` otherwise would.
   */
  login(email: string, password: string) {
    const body: AdminLoginBody = { email, password };
    return send<AdminLoginChallengeResponse | AdminSessionResponse>(
      `${API_PREFIX}/admin/auth/login`,
      {
        method: 'POST',
        body,
        anonymous: true,
      },
    ).then(naming401(ADMIN_AUTH_ERROR.INVALID_CREDENTIALS));
  },

  /**
   * Turns this admin's own two-factor on or off. Costs the current password
   * either way — a live session is not enough to weaken its own sign-in.
   */
  updateMfa(enabled: boolean, password: string) {
    const body: AdminMfaUpdateBody = { enabled, password };
    return authenticated<AdminIdentity>(`${API_PREFIX}/admin/auth/mfa`, {
      method: 'PUT',
      body,
    });
  },

  /** Step two, and the only call that mints a token. Sets the refresh cookie as a side effect. */
  verifyTotp(challengeToken: string, code: string) {
    const body: AdminTotpVerifyBody = { challengeToken, code };
    return send<AdminSessionResponse>(`${API_PREFIX}/admin/auth/totp`, {
      method: 'POST',
      body,
      anonymous: true,
    }).then(namingTotp401);
  },

  /**
   * Restores a session from the cookie at startup.
   *
   * Deliberately not `authenticated` — there is no token to retry with, and a
   * failure here is the ordinary case of somebody arriving signed out.
   */
  restore: renew,

  me() {
    return authenticated<AdminIdentity>(`${API_PREFIX}/admin/auth/me`);
  },

  pendingCounts() {
    return authenticated<AdminPendingCounts>(`${API_PREFIX}/admin/moderation/pending-counts`);
  },

  notifications() {
    return authenticated<AdminNotificationItem[]>(`${API_PREFIX}/admin/moderation/notifications`);
  },

  async logout() {
    await authenticated<void>(`${API_PREFIX}/admin/auth/logout`, { method: 'POST' });
    // Local state goes whatever the server said. A logout that leaves somebody
    // signed in because the network blipped is the wrong failure direction.
    adminSession.clear();
  },
};
